import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, switchMap, take, throwError } from 'rxjs';
import { BaseDomainModel } from '../../../models/base/base-domain-model';
import { BulkDeleteAssetsRequest } from '../../../models/image/requests/bulk-delete-assets-request';
import { BulkDeleteAssetsResponse } from '../../../models/image/responses/bulk-delete-assets-response';
import { BsError } from '../../../models/shared/bs-error';
import { ConnectionStatus } from '../../../models/web-sockets/enums/connection-status';
import { WebSocketService } from '../../../services/web-socket.service';
import { ToastService } from '../../../modules/toasts/service/toast.service';
import { UserDomainModel } from '../../user-domain-model';
import { MediaManagerLibraryContext, MediaManagerStore } from '../store';
import { applyBulkDeletedItems, buildBulkDeleteAssetsRequest } from '../mutations';
import { MediaManagerAssetService } from '../asset/media-manager-asset.service';
import { WebSocketRequestBulkDeleteAssets } from 'src/app/models/web-sockets/messages/send/request/web-socket-request-bulk-delete-assets';

/**
 * WebSocket-based bulk delete: sends the whole selection as one job, applies the effect locally
 * immediately (optimistic), and then reconciles the API response when it comes back.
 *
 * Only errors surface before the job is sent, so nothing was deleted when they do and callers can fall back
 * to the REST per-item delete path in MediaManagerAssetService. Errors after that point are handled by
 * refetching the asset library, since a partial state is possible and the REST fallback would fail its own
 * preflight on items already gone.
 *
 * targetFolderId and deleteAllItems are mutually exclusive and apply to every folder in the request:
 * contents either move to that folder ('ROOT' being the library root) or are deleted. Both are ignored
 * without folders.
 */
@Injectable() // Provided by Logged In Scope
export class MediaManagerBulkDeleteService extends BaseDomainModel {
  constructor(
    private webSocketService: WebSocketService,
    private toastService: ToastService,
    private userDomainModel: UserDomainModel,
    private store: MediaManagerStore,
    private assetService: MediaManagerAssetService
  ) {
    super();
  }

  private readonly companyId = -1;

  // TODO: flip once the WebSocket BulkDeleteAssets action accepts admin connections scoped to a brand. Until then the
  // API rejects the job ("not an authenticated user connection") after the optimistic delete, so nothing is deleted;
  // failing before the send lets callers fall back to the REST per-item delete.
  private readonly webSocketBrandBulkDeleteSupported: boolean = false;

  // TODO - For bulk delete brand assets we won't have a specific company or location id.
  // There will likely be a new definition for WebSocketRequestBulkDeleteAssets
  // whose ctor will probably take just a BrandID.
  bulkDeleteAssets$(
    assetUniqueIds: string[],
    folderIds: string[],
    targetFolderId: string,
    deleteAllItems: boolean
  ): Observable<void> {
    const normalizedAssetUniqueIds = this.assetService.normalizeAssetDeleteIds(assetUniqueIds);
    const normalizedFolderIds = this.assetService.normalizeAssetDeleteIds(folderIds);
    return combineLatest([
      this.store.getActiveLibraryContext$(),
      this.userDomainModel.userId$,
      this.webSocketService.socketConnectionStatus$
    ]).pipe(
      take(1),
      switchMap(([context, userId, connectionStatus]) => {
        const { assetLibrary } = context;
        if (!this.webSocketBrandBulkDeleteSupported) {
          return throwError(() =>
            this.buildBulkDeleteNotSentError('WebSocket bulk delete does not support brands yet.')
          );
        }
        if (connectionStatus !== ConnectionStatus.Connected) {
          return throwError(() => this.buildBulkDeleteNotSentError('WebSocket is not connected.'));
        }
        const request = buildBulkDeleteAssetsRequest(
          normalizedAssetUniqueIds,
          normalizedFolderIds,
          targetFolderId,
          deleteAllItems,
          assetLibrary
        );
        const validationError = request.validationError();
        if (validationError) {
          // REST enforces the same rules, so a fallback would only fail the same way
          return throwError(() => BsError.NewLocalBsError('Delete Items', validationError));
        }
        // TODO - Alex: Verify the correct shape of WebSocketRequestBulkDeleteAssets after
        // backend has it set up for brand specific deletions.
        const mockLocationId = 998;
        this.startBulkDeleteJob(
          new WebSocketRequestBulkDeleteAssets(userId, this.companyId, mockLocationId, request),
          context
        );
        this.applyOptimisticBulkDelete(normalizedAssetUniqueIds, request, context);
        return of(undefined);
      })
    );
  }

  private startBulkDeleteJob(request: WebSocketRequestBulkDeleteAssets, context: MediaManagerLibraryContext): void {
    this.webSocketService.requestObject$(request, BulkDeleteAssetsResponse).subscribeWhileAlive({
      owner: this,
      next: ({ data }) => {
        this.publishBulkDeleteFinishedToast(data);
        this.reloadAssetLibrary(context.brandId);
      },
      error: (err: BsError) => {
        console.info(`MediaManager bulk delete failed: ${err?.message || err}`);
        this.toastService.publishError(err);
        this.reloadAssetLibrary(context.brandId);
      }
    });
  }

  private reloadAssetLibrary(brandId: string): void {
    this.store.refreshBrandAssetLibrary$(brandId).subscribeWhileAlive({
      owner: this,
      error: err => console.info(err?.message || err)
    });
  }

  /** Silent when the job found nothing left to do - a retry or stale selection, already gone to the user. */
  private publishBulkDeleteFinishedToast(result: BulkDeleteAssetsResponse): void {
    this.store.debug.logBulkDeleteFinished(result?.summaryForLog());
    const deletedItems: string[] = [];
    const deletedAssetCount = result?.deletedAssetCount ?? 0;
    const deletedFolderCount = result?.deletedFolderCount ?? 0;
    const movedCount = (result?.movedAssetCount ?? 0) + (result?.movedFolderCount ?? 0);
    if (deletedAssetCount > 0) {
      deletedItems.push(`${deletedAssetCount} media item${deletedAssetCount === 1 ? '' : 's'}`);
    }
    if (deletedFolderCount > 0) {
      deletedItems.push(`${deletedFolderCount} folder${deletedFolderCount === 1 ? '' : 's'}`);
    }
    if (!deletedItems.length && !movedCount) return;
    const deleted = deletedItems.length ? `${deletedItems.join(' and ')} deleted!` : 'Media Manager updated!';
    const moved = movedCount > 0 ? ` ${movedCount} item${movedCount === 1 ? '' : 's'} moved.` : '';
    this.toastService.publishSuccessMessage(`${deleted}${moved}`);
  }

  /** Never reached the API, so nothing was deleted and the caller can fall back to REST. */
  private buildBulkDeleteNotSentError(message: string): BsError {
    return BsError.NewLocalRestfulFallbackError('Delete Items', message);
  }

  /** Falls back to a refetch when the incremental patch will not apply cleanly. */
  private applyOptimisticBulkDelete(
    assetUniqueIds: string[],
    request: BulkDeleteAssetsRequest,
    context: MediaManagerLibraryContext
  ): void {
    const { brandId, filesystem, assetLibrary } = context;
    const inMemoryPatchSucceeded = applyBulkDeletedItems(assetUniqueIds, request, filesystem, assetLibrary);
    if (inMemoryPatchSucceeded) {
      this.store.notifyFilesystemChanged('bulk delete accepted');
    } else {
      this.store.debug.logBulkDeletePatchFallback();
      this.store.refreshBrandAssetLibrary$(brandId).subscribeWhileAlive({
        owner: this,
        error: err => console.info(err?.message || err)
      });
    }
    this.publishBulkDeleteAcceptedToast(assetUniqueIds.length, request.assetFolderIds.length);
  }

  /** Straight to the ToastService: successToasts$ is only consumed by the settings Media Manager view. */
  private publishBulkDeleteAcceptedToast(assetCount: number, folderCount: number): void {
    const deletedItems: string[] = [];
    if (assetCount > 0) deletedItems.push(`${assetCount} media item${assetCount === 1 ? '' : 's'}`);
    if (folderCount > 0) deletedItems.push(`${folderCount} folder${folderCount === 1 ? '' : 's'}`);
    if (!deletedItems.length) return;
    this.toastService.publishInfoMessage(
      `Deleting ${deletedItems.join(' and ')} in the background.`,
      'Delete In Progress'
    );
  }
}
